import {ToggleButtonGroup} from '@wandb/components';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React from 'react';

import {type WeaveMetricThresholdConfig} from '../types';
import {type TimeRange} from './useWeaveMetricChartData';

export const CHART_HEIGHT = 200;

export const TIME_RANGES: TimeRange[] = ['1h', '1d'];

// Blue metric line shared by the calls and spans metric charts.
export const METRIC_LINE_COLOR = '#2196F3';

export type WeaveMetricChartProps = {
  entityName: string;
  projectName: string;
  config: WeaveMetricThresholdConfig;
};

type TimeRangeSelectorProps = {
  range: TimeRange;
  setRange: (r: TimeRange) => void;
};

const TIME_RANGE_ITEMS: {id: TimeRange; children: string}[] = TIME_RANGES.map(
  r => ({id: r, children: r})
);

const isTimeRange = (value: unknown): value is TimeRange =>
  TIME_RANGES.some(r => r === value);

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  range,
  setRange,
}) => (
  <div className="mb-8">
    <ToggleButtonGroup
      aria-label="Chart time range"
      items={TIME_RANGE_ITEMS}
      selectedKeys={[range]}
      selectionMode="single"
      disallowEmptySelection
      size="sm"
      onSelectionChange={keys => {
        const next = Array.from(keys)[0];
        if (isTimeRange(next)) {
          setRange(next);
        }
      }}
    />
  </div>
);

type ChartStatusProps = {
  loading: boolean;
  error: unknown;
  hasData: boolean;
  children: React.ReactNode;
};

export const ChartStatus: React.FC<ChartStatusProps> = ({
  loading,
  error,
  hasData,
  children,
}) => {
  if (loading && !hasData) {
    return (
      <div className="flex h-full items-center justify-center">
        <WaveLoader size="small" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-moon-500 dark:text-wb-text-tertiary">
        Failed to load metric data
      </div>
    );
  }
  if (!hasData) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-moon-500 dark:text-wb-text-tertiary">
        No data in this time range
      </div>
    );
  }
  return <>{children}</>;
};
